import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTour } from "@/contexts/TourContext";
import { Button } from "@/components/ui/button";
import { X, ArrowRight, ArrowLeft } from "lucide-react";

const TOOLTIP_WIDTH = 320;

function computeTooltipPosition(rect, placement) {
  const gap = 16;
  const maxLeft = window.innerWidth - TOOLTIP_WIDTH - 16;
  switch (placement) {
    case "right":
      return { top: Math.max(16, Math.min(window.innerHeight - 220, rect.top)), left: Math.min(maxLeft, rect.right + gap) };
    case "left":
      return { top: Math.max(16, Math.min(window.innerHeight - 220, rect.top)), left: Math.max(16, rect.left - TOOLTIP_WIDTH - gap) };
    case "top":
      return { top: Math.max(16, rect.top - gap - 190), left: Math.min(maxLeft, Math.max(16, rect.left)) };
    default:
      return { top: Math.min(window.innerHeight - 210, rect.bottom + gap), left: Math.min(maxLeft, Math.max(16, rect.left)) };
  }
}

export function OnboardingTour() {
  const { steps, stepIndex, running, next, prev, skip } = useTour();
  const navigate = useNavigate();
  const location = useLocation();
  const [rect, setRect] = useState(null);
  const [ready, setReady] = useState(false);
  const pollRef = useRef(null);

  const step = steps[stepIndex];

  useEffect(() => {
    if (!running || !step) return;
    setReady(false);
    setRect(null);
    if (step.route && location.pathname !== step.route) {
      navigate(step.route);
      return;
    }
    let tries = 0;
    clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      tries += 1;
      const el = document.querySelector(`[data-testid="${step.target}"]`);
      if (el) {
        clearInterval(pollRef.current);
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => {
          setRect(el.getBoundingClientRect());
          setReady(true);
        }, 260);
      } else if (tries > 30) {
        clearInterval(pollRef.current);
        setRect(null);
        setReady(true);
      }
    }, 100);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, stepIndex, location.pathname]);

  useEffect(() => {
    if (!running || !ready) return;
    const onResize = () => {
      const el = step && document.querySelector(`[data-testid="${step.target}"]`);
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, ready, stepIndex]);

  if (!running || !step || !ready) return null;

  const isCenter = step.placement === "center" || !rect;
  const tooltipStyle = isCenter
    ? { top: "50%", left: "50%", transform: "translate(-50%, -50%)" }
    : computeTooltipPosition(rect, step.placement);

  return (
    <div className="fixed inset-0 z-[100]" data-testid="onboarding-tour-overlay">
      {rect ? (
        <div
          className="fixed pointer-events-none transition-all duration-300 rounded-lg"
          style={{
            top: rect.top - 8,
            left: rect.left - 8,
            width: rect.width + 16,
            height: rect.height + 16,
            boxShadow: "0 0 0 9999px rgba(15, 23, 42, 0.62)",
            border: "2px solid #114B5F",
          }}
        />
      ) : (
        <div className="fixed inset-0 bg-slate-900/60" />
      )}

      <div
        className="fixed z-[101] animate-in fade-in zoom-in-95"
        style={{ width: TOOLTIP_WIDTH, ...tooltipStyle }}
        data-testid="onboarding-tour-tooltip"
      >
        <div className="bg-card border shadow-xl rounded-xl p-5 relative">
          <button
            onClick={skip}
            className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
            data-testid="tour-close-btn"
            aria-label="Fechar tour"
          >
            <X size={16} />
          </button>
          <p className="text-[11px] uppercase tracking-widest text-primary font-semibold mb-1">
            Passo {stepIndex + 1} de {steps.length}
          </p>
          <h3 className="font-heading font-semibold text-base mb-1.5 pr-4">{step.title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">{step.text}</p>
          <div className="flex items-center justify-between mt-4">
            <button onClick={skip} className="text-xs text-muted-foreground hover:text-foreground" data-testid="tour-skip-btn">
              Pular tour
            </button>
            <div className="flex gap-2">
              {stepIndex > 0 && (
                <Button variant="outline" size="sm" onClick={prev} data-testid="tour-prev-btn">
                  <ArrowLeft size={14} />
                </Button>
              )}
              <Button size="sm" onClick={next} className="gap-1" data-testid="tour-next-btn">
                {stepIndex + 1 === steps.length ? "Concluir" : "Próximo"} <ArrowRight size={14} />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
